/**
 * Shopping candidate expansion shared by the browser and Edge Functions.
 *
 * Inventory identity remains the confirmed `same_item` closure. Shopping is
 * deliberately broader by exactly one relationship hop: direct confirmed
 * `purchasing_alternative` neighbours of that closure, with each neighbour
 * expanded through its OWN `same_item` closure. Alternative edges are never
 * unioned with each other, so Tito's -> Absolut -> Grey Goose does not become
 * one transitive product or count group.
 */

export const EQUIVALENT_RELATIONSHIP_TYPE = "same_item";
export const ALTERNATIVE_RELATIONSHIP_TYPE = "purchasing_alternative";
export const CONFIRMED_RELATIONSHIP_STATUS = "confirmed";

const idAscending = (left, right) => {
  const l = Number(left);
  const r = Number(right);
  if (Number.isFinite(l) && Number.isFinite(r) && l !== r) return l - r;
  return String(left).localeCompare(String(right), undefined, { numeric: true });
};

const validEndpoint = (value) => value !== null && value !== undefined && String(value) !== "";

const relationshipIsLive = (row) => (
  row?.item_a?.is_active !== false && row?.item_b?.is_active !== false
);

/** Connected components over confirmed Equivalent rows only. */
export function buildEquivalentGroups(relationships = []) {
  const parent = new Map();
  const ensure = (id) => {
    const key = String(id);
    if (!parent.has(key)) parent.set(key, key);
    return key;
  };
  const find = (id) => {
    const key = ensure(id);
    const current = parent.get(key);
    if (current === key) return key;
    const root = find(current);
    parent.set(key, root);
    return root;
  };
  const union = (left, right) => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) parent.set(rightRoot, leftRoot);
  };

  (Array.isArray(relationships) ? relationships : []).forEach((row) => {
    if (!row || row.relationship_type !== EQUIVALENT_RELATIONSHIP_TYPE) return;
    if (row.status !== CONFIRMED_RELATIONSHIP_STATUS || !relationshipIsLive(row)) return;
    if (!validEndpoint(row.item_a_id) || !validEndpoint(row.item_b_id)) return;
    if (String(row.item_a_id) === String(row.item_b_id)) return;
    union(row.item_a_id, row.item_b_id);
  });

  const membersByRoot = new Map();
  parent.forEach((_value, id) => {
    const root = find(id);
    if (!membersByRoot.has(root)) membersByRoot.set(root, []);
    membersByRoot.get(root).push(id);
  });

  const groupIdByItemId = new Map();
  const membersByGroupId = new Map();
  membersByRoot.forEach((members) => {
    const sorted = [...members].sort(idAscending);
    const groupId = sorted[0];
    membersByGroupId.set(groupId, sorted);
    sorted.forEach((id) => groupIdByItemId.set(id, groupId));
  });
  return { groupIdByItemId, membersByGroupId };
}
export function equivalentGroupIdFor(equivalentGroups, itemId) {
  if (!validEndpoint(itemId)) return null;
  return equivalentGroups?.groupIdByItemId?.get?.(String(itemId)) || String(itemId);
}

export function equivalentMemberIdsFor(equivalentGroups, itemId) {
  const groupId = equivalentGroupIdFor(equivalentGroups, itemId);
  if (groupId == null) return [];
  const members = equivalentGroups?.membersByGroupId?.get?.(groupId);
  return members?.length ? [...members] : [String(itemId)];
}

export function equivalentMembersSetFor(equivalentGroups, itemId) {
  if (!equivalentGroups || !validEndpoint(itemId)) return null;
  const groupId = equivalentGroups.groupIdByItemId?.get?.(String(itemId));
  if (!groupId) return null;
  const members = equivalentGroups.membersByGroupId?.get?.(groupId);
  return members ? new Set(members) : null;
}

/** Pool only Equivalent options. Used by count identity and legacy callers. */
export function expandOptionsAcrossEquivalentGroups(optionsByItemId = {}, equivalentGroups = null) {
  const source = optionsByItemId || {};
  const expanded = {};
  Object.keys(source).forEach((itemId) => { expanded[itemId] = source[itemId]; });
  if (!equivalentGroups?.membersByGroupId?.size) return expanded;

  equivalentGroups.membersByGroupId.forEach((members) => {
    members.forEach((itemId) => {
      const seen = new Set();
      const pool = [];
      const push = (options) => {
        (Array.isArray(options) ? options : []).forEach((option) => {
          if (!validEndpoint(option?.id)) return;
          const key = String(option.id);
          if (seen.has(key)) return;
          seen.add(key);
          pool.push(option);
        });
      };
      push(source[itemId]);
      members.forEach((memberId) => { if (memberId !== itemId) push(source[memberId]); });
      if (pool.length) expanded[itemId] = pool;
    });
  });
  return expanded;
}

const groupPairKey = (left, right) => [String(left), String(right)].sort(idAscending).join("|");

/**
 * Build the one-hop shopping graph. Pair precedence is enforced here: an
 * Alternative whose endpoints already share an Equivalent closure is omitted
 * from shopping and projection, while the stored row remains untouched.
 */
export function buildShoppingCandidateGraph(relationships = []) {
  const rows = Array.isArray(relationships) ? relationships : [];
  const equivalentGroups = buildEquivalentGroups(rows);
  const alternativeGroupIdsByGroupId = new Map();
  const alternativeRowsByGroupPair = new Map();

  rows.forEach((row) => {
    if (!row || row.relationship_type !== ALTERNATIVE_RELATIONSHIP_TYPE) return;
    if (row.status !== CONFIRMED_RELATIONSHIP_STATUS || !relationshipIsLive(row)) return;
    if (!validEndpoint(row.item_a_id) || !validEndpoint(row.item_b_id)) return;
    const left = equivalentGroupIdFor(equivalentGroups, row.item_a_id);
    const right = equivalentGroupIdFor(equivalentGroups, row.item_b_id);
    if (left == null || right == null || left === right) return;
    if (!alternativeGroupIdsByGroupId.has(left)) alternativeGroupIdsByGroupId.set(left, new Set());
    if (!alternativeGroupIdsByGroupId.has(right)) alternativeGroupIdsByGroupId.set(right, new Set());
    alternativeGroupIdsByGroupId.get(left).add(right);
    alternativeGroupIdsByGroupId.get(right).add(left);
    const key = groupPairKey(left, right);
    if (!alternativeRowsByGroupPair.has(key)) alternativeRowsByGroupPair.set(key, []);
    alternativeRowsByGroupPair.get(key).push(row);
  });

  return { equivalentGroups, alternativeGroupIdsByGroupId, alternativeRowsByGroupPair };
}

export function shoppingCandidateItemIdsForItem(itemId, graph = null) {
  if (!validEndpoint(itemId)) return [];
  const equivalentGroups = graph?.equivalentGroups || buildEquivalentGroups([]);
  const primaryGroupId = equivalentGroupIdFor(equivalentGroups, itemId);
  const ids = new Set(equivalentMemberIdsFor(equivalentGroups, itemId));
  (graph?.alternativeGroupIdsByGroupId?.get?.(primaryGroupId) || new Set()).forEach((groupId) => {
    const members = equivalentGroups.membersByGroupId?.get?.(groupId) || [groupId];
    members.forEach((id) => ids.add(String(id)));
  });
  return [...ids].sort(idAscending);
}

const lookup = (collection, key) => collection?.get?.(String(key)) ?? collection?.[String(key)] ?? null;

const activeOptionsForMembers = (memberIds, optionsByItemId) => {
  const seen = new Set();
  const options = [];
  memberIds.forEach((itemId) => {
    (optionsByItemId?.[itemId] || []).forEach((option) => {
      if (!option || option.status !== "active" || !validEndpoint(option.id)) return;
      const key = String(option.id);
      if (seen.has(key)) return;
      seen.add(key);
      options.push(option);
    });
  });
  return options;
};

const canonicalMemberId = (groupId, memberIds, optionsByItemId, canonicalItemIdByGroupId) => {
  const stored = lookup(canonicalItemIdByGroupId, groupId);
  if (stored != null && memberIds.includes(String(stored))) return String(stored);
  let recentId = null;
  let recentAt = -Infinity;
  memberIds.forEach((itemId) => {
    (optionsByItemId?.[itemId] || []).forEach((option) => {
      const at = Date.parse(option?.last_operational_source_at ?? "");
      if (Number.isFinite(at) && at > recentAt) {
        recentAt = at;
        recentId = itemId;
      }
    });
  });
  return recentId || [...memberIds].sort(idAscending)[0] || String(groupId);
};

const describeGroup = ({
  groupId, relationshipType, equivalentGroups, itemsById, optionsByItemId,
  canonicalItemIdByGroupId, relationshipRows = [],
}) => {
  const memberIds = equivalentGroups.membersByGroupId?.get?.(groupId) || [String(groupId)];
  const ids = [...memberIds].map(String).sort(idAscending);
  const canonicalId = canonicalMemberId(groupId, ids, optionsByItemId, canonicalItemIdByGroupId);
  return {
    groupId: String(groupId),
    relationshipType,
    canonicalItemId: canonicalId,
    canonicalItem: lookup(itemsById, canonicalId),
    itemIds: ids,
    items: ids.map((id) => lookup(itemsById, id)).filter(Boolean),
    purchaseOptions: activeOptionsForMembers(ids, optionsByItemId),
    relationshipRows,
  };
};

/**
 * Authoritative shopping expansion for one counted item.
 *
 * The primary group is the Count Item's Equivalent closure. Substitute groups
 * are direct confirmed Alternatives, each expanded only through its own
 * Equivalent closure. The returned option-role map lets planners display all
 * candidates while restricting automatic selection to primary options.
 */
export function getShoppingCandidatesForItem(itemId, {
  graph = null,
  relationships = [],
  itemsById = {},
  optionsByItemId = {},
  canonicalItemIdByGroupId = {},
} = {}) {
  const resolvedGraph = graph || buildShoppingCandidateGraph(relationships);
  const equivalentGroups = resolvedGraph.equivalentGroups;
  const primaryGroupId = equivalentGroupIdFor(equivalentGroups, itemId);
  if (primaryGroupId == null) {
    return { primaryGroup: null, substituteGroups: [], purchaseOptions: [], optionRoleById: {} };
  }
  const primaryGroup = describeGroup({
    groupId: primaryGroupId,
    relationshipType: "equivalent",
    equivalentGroups,
    itemsById,
    optionsByItemId,
    canonicalItemIdByGroupId,
  });
  const substituteGroups = [...(resolvedGraph.alternativeGroupIdsByGroupId.get(primaryGroupId) || [])]
    .sort(idAscending)
    .map((groupId) => describeGroup({
      groupId,
      relationshipType: "alternative",
      equivalentGroups,
      itemsById,
      optionsByItemId,
      canonicalItemIdByGroupId,
      relationshipRows: resolvedGraph.alternativeRowsByGroupPair.get(groupPairKey(primaryGroupId, groupId)) || [],
    }));

  const optionRoleById = {};
  primaryGroup.purchaseOptions.forEach((option) => { optionRoleById[String(option.id)] = "primary"; });
  substituteGroups.forEach((group) => group.purchaseOptions.forEach((option) => {
    optionRoleById[String(option.id)] = "substitute";
  }));
  return {
    primaryGroup,
    substituteGroups,
    purchaseOptions: [
      ...primaryGroup.purchaseOptions,
      ...substituteGroups.flatMap((group) => group.purchaseOptions),
    ],
    optionRoleById,
  };
}
